// js/fireworks-shared/fireworks-controller-2.js
// Shared FireworksController for both Desktop and VR stock-cube-visualizer components.
//
// Usage:
//   this.fireworksController = new FireworksController(this);        // Desktop (listens on document)
//   this.fireworksController = new FireworksController(this, this.el); // VR (listens on component element)
//   this.fireworksController.init();
//   // on remove:
//   this.fireworksController.destroy();

class FireworksController {
    /**
     * @param {object} visualizer  — the AFRAME component instance (has .stockBars, .data, .configSettings, .getConfigSetting, .getCachedStockInfoMap)
     * @param {Element} [configureTarget]  — element that emits 'configure-changed'. Defaults to document.
     */
    constructor(visualizer, configureTarget = null) {
        this.visualizer = visualizer;
        this._configureTarget = configureTarget || document;
        this._intervalId = null;
        this._setupTimeoutId = null;
        this._staggerTimeoutIds = new Set();
        this._cachedChartWrapper = null;
        this._excludedFireworkSources = new Set(['dividend', 'dividend-sec', 'sip', 'sip-sec', 'mmap']);
        this.settingTodaysChangePerc = 3;
        this._watchlistFetchInProgress = false;

        this._onWatchlistChanged = this._onWatchlistChanged.bind(this);
        this._onConfigureChanged = this._onConfigureChanged.bind(this);
        this._onVisibilityChanged = this._onVisibilityChanged.bind(this);
        this._onSessionChanged = this._onSessionChanged.bind(this);
        
        this._staticIndicators = new Map();
    }

    init() {
        document.body.addEventListener('watchlist-data-loaded', this._onWatchlistChanged);
        this._configureTarget.addEventListener('configure-changed', this._onConfigureChanged);
        document.addEventListener('visibilitychange', this._onVisibilityChanged);
        document.addEventListener('auth:session-changed', this._onSessionChanged);
        this.updateThreshold();
    }

    destroy() {
        document.body.removeEventListener('watchlist-data-loaded', this._onWatchlistChanged);
        this._configureTarget.removeEventListener('configure-changed', this._onConfigureChanged);
        document.removeEventListener('visibilitychange', this._onVisibilityChanged);
        document.removeEventListener('auth:session-changed', this._onSessionChanged);
        this.clearAll();
    }

    updateThreshold() {
        const threshold = this.visualizer.getConfigSetting('fireworkThreshold', '+10%');
        const parsed = parseInt(String(threshold).replace(/\+/g, '').replace(/%/g, ''), 10);
        this.settingTodaysChangePerc = isNaN(parsed) ? 10 : parsed;
    }

    // ─── Event handlers ───

    _onWatchlistChanged() {
        if (this._isAnimating()) return;
        this.requestRefresh(500);
    }

    _onConfigureChanged() {
        this.updateThreshold();
        if (this._isAnimating()) return;
        this.requestRefresh(500);
    }

    _onVisibilityChanged() {
        if (!document.hidden && !this._isAnimating()) {
            this.requestRefresh(1000);
        }
    }

    _onSessionChanged(e) {
        const session = e?.detail?.session;
        if (session && (session.access_token || session.app_session)) {
            this.requestRefresh(1500);
        } else {
            this.clearAll();
        }
    }

    // ─── Timer management ───

    clearAll() {
        this._clearTimers();
        if (window.Fireworks) {
            window.Fireworks.emergencyStopFireworks();
        }
        this._clearStaticIndicators();
    }

    _clearStaticIndicators() {
        for (const [ticker, element] of this._staticIndicators) {
            if (element && element.parentNode) {
                element.parentNode.removeChild(element);
            }
        }
        this._staticIndicators.clear();
    }

    clearFireworkIntervals() {
        this.clearAll();
    }

    _clearTimers() {
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
        if (this._setupTimeoutId) {
            clearTimeout(this._setupTimeoutId);
            this._setupTimeoutId = null;
        }
        for (const id of this._staggerTimeoutIds) {
            clearTimeout(id);
        }
        this._staggerTimeoutIds.clear();
    }

    // ─── Refresh entry points ───

    requestRefresh(delayMs = 1000) {
        this._clearTimers();
        if (window.Fireworks) {
            window.Fireworks.emergencyStopFireworks();
        }
        this._clearStaticIndicators();

        this._setupTimeoutId = setTimeout(() => {
            this._setupTimeoutId = null;
            this._startFireworks();
        }, delayMs);
    }

    setupPostAnimation() {
        this.requestRefresh(1000);
    }

    // ─── Core fireworks logic ───

    _startFireworks() {
        this._doLaunch();

        this._intervalId = setInterval(() => {
            this._doLaunch();
        }, 20000);
    }

    _doLaunch() {
        const ctx = this._canFireFireworksGlobal();
        if (!ctx) {
            this._clearStaticIndicators();
            return;
        }

        if (!this.visualizer.stockBars || this.visualizer.stockBars.size === 0) return;
        if (this._isAnimating()) return;
        if (!window.Fireworks) return;

        const stockMap = this.visualizer.getCachedStockInfoMap();
        const currentValidTickers = new Set();

        for (const [ticker, barInfo] of this.visualizer.stockBars) {
            if (!barInfo || !barInfo.bar || !barInfo.bar.object3D) continue;
            if (typeof barInfo.x !== 'number' || typeof barInfo.z !== 'number') continue;
            if (!ctx.watchlistTickerSet.has(ticker)) continue;

            const stock = stockMap.get(ticker);
            if (!stock) continue;
            if (typeof stock.todaysChangePerc !== 'number') continue;
            if (stock.todaysChangePerc < this.settingTodaysChangePerc) continue;

            currentValidTickers.add(ticker);
            this._launchBurstForBar(barInfo);
        }

        this._syncStaticIndicators(currentValidTickers);
    }

    _syncStaticIndicators(validTickers) {
        for (const [ticker, element] of this._staticIndicators) {
            if (!validTickers.has(ticker)) {
                if (element && element.parentNode) {
                    element.parentNode.removeChild(element);
                }
                this._staticIndicators.delete(ticker);
            }
        }

        /* for (const ticker of validTickers) {
            if (!this._staticIndicators.has(ticker)) {
                const barInfo = this.visualizer.stockBars.get(ticker);
                if (barInfo && barInfo.primaryLabel) {
                    const asterisk = document.createElement('a-troika-text');
                    asterisk.setAttribute('value', '*');
                    asterisk.setAttribute('class', 'billboard-y');
                    asterisk.setAttribute('color', '#FFD700');
                    
                    let labelFontSize = parseFloat(barInfo.primaryLabel.getAttribute('font-size'));
                    if (isNaN(labelFontSize) || labelFontSize <= 0) labelFontSize = 0.15;
                    
                    asterisk.setAttribute('font-size', (labelFontSize * 0.6).toFixed(3));
                    asterisk.setAttribute('align', 'left');
                    
                    asterisk.setAttribute('animation__color', 'property: color; from: #FFD700; to: #FF4500; dur: 800; dir: alternate; loop: true; easing: easeInOutSine');
                    asterisk.setAttribute('animation__scale', 'property: scale; from: 1 1 1; to: 1.4 1.4 1.4; dur: 800; dir: alternate; loop: true; easing: easeInOutSine');
                    
                    asterisk.setAttribute('fireworks-asterisk', '');
                    
                    const tickerStr = (barInfo.lastLabel || ticker || '').split('\n')[0];
                    const rightEdge = (tickerStr.length / 2) * (labelFontSize * 0.55);
                    const offsetX = rightEdge + 0.02;
                    
                    const offsetY = labelFontSize * 0.8;
                    asterisk.setAttribute('position', `${offsetX} ${offsetY} 0.01`);
                    
                    barInfo.primaryLabel.appendChild(asterisk);
                    this._staticIndicators.set(ticker, asterisk);
                }
            }
        } */
    }

    _launchBurstForBar(barInfo) {
        if (!window.Fireworks) return;
        if (!barInfo || !barInfo.bar || !barInfo.bar.object3D) return;

        const isPositive = (typeof barInfo.lastHeight === 'number') ? (barInfo.lastHeight >= 0) : true;
        const activeHeight = Math.abs(barInfo.lastHeight || 0.2);
        const finalTravelHeight = 0.15;

        let fireworkY;
        if (typeof this.visualizer.zeroPlane === 'number') {
            fireworkY = isPositive
                ? this.visualizer.zeroPlane + activeHeight
                : this.visualizer.zeroPlane;
        } else {
            const halfHeight = activeHeight / 2;
            fireworkY = isPositive
                ? (barInfo.bar.object3D.position.y || 0.2) + halfHeight
                : (barInfo.bar.object3D.position.y || 0.2) - halfHeight;
        }

        barInfo.fireworkTravelHeight = finalTravelHeight;

        window.Fireworks.launchFireworkBurstAtPosition(
            barInfo,
            barInfo.x,
            fireworkY,
            -barInfo.z
        );
    }

    // ─── Helpers ───

    _isAnimating() {
        try {
            const tl = this.visualizer.data?.timelineEntity?.components?.['timeline-control'];
            if (tl && tl.isAnimating) return true;
        } catch (e) { /* defensive */ }
        return false;
    }

    _getChartWrapper() {
        if (!this._cachedChartWrapper) {
            this._cachedChartWrapper = document.querySelector('.chart-wrapper');
        }
        return this._cachedChartWrapper;
    }

    _getWatchlistTickerSet() {
        const watchlistState = window.WatchlistApp?.State;
        if (!watchlistState || !watchlistState.allWatchlists) return null;

        const set = new Set();
        for (const wl of watchlistState.allWatchlists) {
            if (wl.tickers && Array.isArray(wl.tickers)) {
                for (const t of wl.tickers) set.add(t);
            }
        }
        return set.size > 0 ? set : null;
    }

    _canFireFireworksGlobal() {
        if (document.hidden) return null;

        // Chart-wrapper visibility check — Desktop only. In VR there is no .chart-wrapper.
        const cw = this._getChartWrapper();
        if (cw && cw.style.display === 'none') return null;

        if (!this.visualizer.getConfigSetting('isFireworkOn', false)) return null;

        if (this._excludedFireworkSources.has(this.visualizer.data.dataSource)) return null;

        if (!(window.currentSession && (window.currentSession.access_token || window.currentSession.app_session))) return null;

        if (!this.visualizer.data.interval || this.visualizer.data.interval.toLowerCase() !== '1d') return null;

        const watchlistState = window.WatchlistApp?.State;
        if (!watchlistState || !watchlistState.allWatchlists || watchlistState.allWatchlists.length === 0) {
            if (window.WatchlistApp && window.WatchlistApp.Manager && !this._watchlistFetchInProgress) {
                this._watchlistFetchInProgress = true;
                window.WatchlistApp.Manager.updateWatchlistState().then(({ watchlists }) => {
                    this._watchlistFetchInProgress = false;
                    if (watchlists && watchlists.length > 0) {
                        this.requestRefresh(500);
                    }
                }).catch(() => {
                    this._watchlistFetchInProgress = false;
                });
            }
            return null;
        }

        const tickerSet = this._getWatchlistTickerSet();
        if (!tickerSet) return null;

        return { watchlistTickerSet: tickerSet };
    }
}

window.FireworksController = FireworksController;

if (typeof AFRAME !== 'undefined' && !AFRAME.components['fireworks-asterisk']) {
    AFRAME.registerComponent('fireworks-asterisk', {
        init: function() {
            this.el.addEventListener('troika-text-ready', () => {
                if (!this.el.object3D) return;
                this.el.object3D.traverse((child) => {
                    if (child.material) {
                        const mats = Array.isArray(child.material) ? child.material : [child.material];
                        mats.forEach(m => {
                            if (!m.userData) m.userData = {};
                            m.userData.bloom = true;
                        });
                    }
                });
            });
        }
    });
}
